import React from "react";

import cx from "classnames";

import type { StarChoices } from "../ResponseForm/validation";
import animationData from "./css/feedback-respond-animation-test.json";
import { ReactComponent as StarOffDark } from "./css/star-off-dark.svg";
import { ReactComponent as StarOff } from "./css/star-off.svg";
import { ReactComponent as StarOn } from "./css/star-on.svg";
import RangeInput, { type RangeInputConsumerProps, type RangeInputItemProps } from "./RangeInput";

import styles from "./css/StarInput.module.css";

const STAR_VALUES = ["none", "1", "2", "3", "4", "5"] as const;
export type StarValue = (typeof STAR_VALUES)[number];

const valueToChoiceMap: { [k in Exclude<StarValue, "none">]: StarChoices[number] } = {
	1: { label: "1 / 5", value: 2 },
	2: { label: "2 / 5", value: 4 },
	3: { label: "3 / 5", value: 6 },
	4: { label: "4 / 5", value: 8 },
	5: { label: "5 / 5", value: 10 },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isStarValue(value: any): value is StarValue {
	return STAR_VALUES.includes(value);
}

type StarProps = { selected?: boolean; readonly?: boolean; value: StarValue; showValue?: boolean } & Omit<
	React.SVGProps<SVGSVGElement>,
	"selected" | "readonly"
>;

export function Star({ className, selected, readonly, value, showValue }: StarProps) {
	let Component = selected ? StarOn : StarOff;
	const html = document.documentElement;
	if (html && html.dataset.theme == "theme-dark" && !selected) {
		Component = StarOffDark;
	}

	return (
		<div
			className={cx(styles.star, className, {
				selected: selected,
				readonly: readonly,
			})}
		>
			<Component />
			<span className={cx({ [styles.hidden]: !showValue })}>{value}</span>
		</div>
	);
}

type StarWrapperProps = RangeInputItemProps<StarValue>;

export function StarWrapper({ selected, selectedValue, value, hoveredValue, readonly }: StarWrapperProps) {
	const numberValue = Number.parseInt(value, 10);
	const hovered = hoveredValue == value;
	const lessThanSelected = numberValue < Number.parseInt(selectedValue, 10);
	const lessThanHovered = hoveredValue && numberValue < Number.parseInt(hoveredValue, 10);

	return (
		<Star selected={selected || lessThanSelected || hovered || lessThanHovered} readonly={readonly} value={value} />
	);
}

export default function StarInput(props: RangeInputConsumerProps<StarValue>) {
	return (
		<RangeInput<StarValue>
			itemComponent={StarWrapper}
			animationOptions={{
				animationData,
				rendererSettings: {
					className: styles.lottie,
				},
			}}
			options={["1", "2", "3", "4", "5"]}
			lottieProps={{
				width: "3em",
				height: "3em",
				// @ts-ignore
				style: { overflow: "visible !important", position: "absolute", transform: "translate(-20%, -10%)" },
			}}
			{...props}
		/>
	);
}

StarInput.valueToChoiceMap = valueToChoiceMap;
